// client/src/components/auth/BankReferralRegistration.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageSelector from '../layout/LanguageSelector';
import { sanitizeInput } from '../utils/sanitize';

const BankReferralRegistration = () => {

  const { t, i18n } = useTranslation(); // ✅ Aggiungere i18n
  const currentLanguage = i18n.language.startsWith('it') ? 'it' : 'en'; // ✅ Aggiungere questa riga
  const { token } = useParams();
  const navigate = useNavigate();

  // States
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    position: '',
    acceptedTerms: false
  });

  const validateInvite = useCallback(async () => {
    try {
      setValidating(true);
      //console.log('Validating bank referral invite token:', token);
      
      // FIX: Use correct endpoint
      const response = await axios.get(`/api/company/bank-referral/validate/${token}`);
      //console.log('Bank referral invite validation response:', response.data);
      
      if (response.data.success && response.data.invitation) {
        setInviteData(response.data.invitation);
        setFormData(prev => ({
          ...prev,
          email: response.data.invitation.email,
          firstName: response.data.invitation.firstName,
          lastName: response.data.invitation.lastName
        }));
        setError('');
      } else {
        setError('Invalid invitation data received');
      }
    } catch (err) {
      console.error('Bank referral invite validation error:');
      const errorMessage = err.response?.data?.message || t('bankReferral.auth.invalidLink');
      setError(errorMessage);
    } finally {
      setValidating(false);
    }
  }, [token, t]);

  useEffect(() => {
    validateInvite();
  }, [validateInvite]);

  const onChange = e => {
    if (e.target.type === 'checkbox') {
      setFormData({ ...formData, [e.target.name]: e.target.checked });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
    
    if (error) {
      setError('');
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    //console.log('Bank referral registration form submission started', formData);
    
    // Validation
    if (!formData.position.trim()) {
      return setError(t('bankReferral.auth.positionRequired') || 'Position is required');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return setError(t('bankReferral.auth.passwordMismatch') || 'Passwords do not match');
    }
    
    if (formData.password.length < 10) {
      return setError(t('bankReferral.auth.passwordTooShort') || 'Password must be at least 10 characters');
    }
    
    if (!/[A-Z]/.test(formData.password) || 
        !/[a-z]/.test(formData.password) || 
        !/[0-9]/.test(formData.password) || 
        !/[^A-Za-z0-9]/.test(formData.password)) {
      return setError(t('bankReferral.auth.passwordWeak') || 'Password must contain uppercase, lowercase, number and special character');
    }
    
    if (!formData.acceptedTerms) {
      return setError(t('bankReferral.auth.acceptTermsRequired') || 'You must accept the terms and conditions');
    }
    
    setSubmitting(true);
    
    try {
      const sanitizedData = {
        password: formData.password,
        phone: sanitizeInput(formData.phone.trim()),
        position: sanitizeInput(formData.position.trim()),
        acceptedTerms: formData.acceptedTerms
      };
      
      //console.log('Sending bank referral registration data:', { ...sanitizedData, password: '[HIDDEN]' });
      
      // FIX: Use correct endpoint
      const response = await axios.post(`/api/company/bank-referral/register/${token}`, sanitizedData);
      
      //console.log('Bank referral registration successful:', response.data);
      
      // Show success page instead of auto-login
      setRegistrationComplete(true);
      
    } catch (err) {
      console.error('Bank referral registration error:');
      const errorMessage = err.response?.data?.message || t('bankReferral.auth.registrationFailed') || 'Registration failed';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Loading state
  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('bankReferral.auth.validating') || 'Validating invitation...'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !inviteData) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>{t('bankReferral.auth.errorTitle') || 'Invitation Error'}</h2>
          <div className="alert alert-danger">{error}</div>
          <Link to="/login" className="btn btn-primary btn-block">
            {t('bankReferral.auth.goToLogin') || 'Go to Login'}
          </Link>
        </div>
      </div>
    );
  }

  // Success state - NEW PAGE
  if (registrationComplete) {
    return (
      <div className="auth-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        <div className="auth-card">
          <div className="success-content">
            <div className="success-icon">
              <div className="checkmark">✓</div>
            </div>
            
            <h2>{t('bankReferral.auth.successTitle') || 'Registration Completed Successfully!'}</h2>
            
            <p className="success-message">
              {t('bankReferral.auth.successMessage') || 'Welcome to the Euracle platform! You are now registered as a bank referral.'}
            </p>
            
            {inviteData && (
              <div className="success-organization">
                <p>
                  <strong>{t('bankReferral.auth.yourBank') || 'Your Bank'}:</strong> {inviteData.bankName}<br/>
                  <strong>{t('bankReferral.auth.partnerCompany') || 'Partner Company'}:</strong> {inviteData.companyName}
                </p>
              </div>
            )}
            
            <button 
              onClick={handleGoToLogin}
              className="btn btn-primary btn-block btn-large"
            >
              {t('bankReferral.auth.goToLoginButton') || 'Go to Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card">
        <h2>{t('bankReferral.auth.title') || 'Complete Your Bank Referral Registration'}</h2>
        
        {inviteData && (
          <div className="invite-info">
            <p>{t('bankReferral.auth.invitedBy', { 
              name: inviteData.companyName 
            }) || `Invited by ${inviteData.companyName}`}</p>
            <p><strong>{t('bankReferral.auth.bankLabel') || 'Bank'}:</strong> {inviteData.bankName}</p>
            <div className="organization-name">{inviteData.companyName}</div>
          </div>
        )}
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={onSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('bankReferral.auth.firstName') || 'First Name'}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                readOnly
                className="form-control-readonly"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('bankReferral.auth.lastName') || 'Last Name'}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                readOnly
                className="form-control-readonly"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('bankReferral.auth.emailAddress') || 'Email Address'}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              readOnly
              className="form-control-readonly"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">{t('bankReferral.auth.phoneNumber') || 'Phone Number'}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={onChange}
                placeholder={t('bankReferral.auth.phonePlaceholder') || 'Your phone number (optional)'}
                disabled={submitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="position">{t('bankReferral.auth.position') || 'Position'} *</label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={onChange}
                placeholder={t('bankReferral.auth.positionPlaceholder') || 'Your position at the bank'}
                required
                disabled={submitting}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('bankReferral.auth.password') || 'Password'} *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={onChange}
                placeholder={t('bankReferral.auth.passwordPlaceholder') || 'Create a secure password'}
                required
                minLength="10"
                disabled={submitting}
              />
              <small className="form-text text-muted">
                {t('bankReferral.auth.passwordRequirements') || 'Password must be at least 10 characters with uppercase, lowercase, number and special character'}
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('bankReferral.auth.confirmPassword') || 'Confirm Password'} *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={onChange}
                placeholder={t('bankReferral.auth.confirmPasswordPlaceholder') || 'Confirm your password'}
                required
                disabled={submitting}
              />
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <div className="checkbox-label">
              <input
                type="checkbox"
                id="acceptedTerms"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={onChange}
                required
                disabled={submitting}
              />
              <span>
                {t('bankReferral.auth.acceptTerms') || 'I accept the'}{' '}
                <a 
                  href={currentLanguage === 'en' ? "/Termini e Condizioni di Utilizzo_eng.pdf" : "/Termini e Condizioni di Utilizzo_ita.pdf"}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="primary-link"
                >
                  {t('bankReferral.auth.termsConditions') || 'Terms & Conditions'}
                </a>
                {' , '}
                <a 
                  href={currentLanguage === 'en' ? "/Informativa Privacy_eng.pdf" : "/Informativa Privacy_ita.pdf"}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="primary-link"
                >
                  {t('bankReferral.auth.privacyPolicy') || 'Privacy Policy'}
                </a>
              </span>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={submitting || !inviteData}
          >
            {submitting ? 
              (t('bankReferral.auth.registering') || 'Registering...') : 
              (t('bankReferral.auth.completeRegistration') || 'Complete Registration')
            }
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            {t('bankReferral.auth.alreadyAccount') || 'Already have an account?'}{' '}
            <Link to="/login">{t('bankReferral.auth.loginHere') || 'Login here'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BankReferralRegistration;